// src/components/workAccountability/waControls.tsx
//
// Small styled controls for the Work Accountability UX, matching existing LevelUp
// idioms (blue-600 accent, rounded-lg, focus rings). No new design language.

import { titleCase } from './waFormat';

interface SegmentedProps<T extends string> {
  value: T;
  options: readonly T[];
  onChange: (v: T) => void;
  label?: string;
  labelFor?: (v: T) => string;
  disabled?: boolean;
  idPrefix?: string;
}

/** Pill-button segmented control (aria radiogroup) — matches RecurrenceFields. */
export function Segmented<T extends string>({ value, options, onChange, label, labelFor, disabled, idPrefix }: SegmentedProps<T>) {
  return (
    <div>
      {label && <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>}
      <div role="radiogroup" aria-label={label} className="inline-flex flex-wrap gap-2">
        {options.map((opt) => {
          const selected = opt === value;
          return (
            <button
              key={opt}
              type="button"
              role="radio"
              aria-checked={selected}
              disabled={disabled}
              id={idPrefix ? `${idPrefix}-${opt}` : undefined}
              onClick={() => onChange(opt)}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed ${
                selected ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {labelFor ? labelFor(opt) : titleCase(opt)}
            </button>
          );
        })}
      </div>
    </div>
  );
}

interface ToggleProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  description?: string;
  disabled?: boolean;
}

/** role="switch" toggle — matches RecordCallToggle (blue accent). */
export function Toggle({ checked, onChange, label, description, disabled }: ToggleProps) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="text-sm font-medium text-gray-800">{label}</div>
        {description && <div className="text-xs text-gray-500">{description}</div>}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
          checked ? 'bg-blue-600' : 'bg-gray-300'
        }`}
      >
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-5' : 'translate-x-0.5'}`}
        />
      </button>
    </div>
  );
}

/** Inline success/error banner — the app's toast substitute (role="alert"). */
export function Banner({ kind, message, testId }: { kind: 'success' | 'error' | 'info'; message: string; testId?: string }) {
  const styles =
    kind === 'success'
      ? 'bg-green-50 border-green-200 text-green-800'
      : kind === 'error'
        ? 'bg-red-50 border-red-200 text-red-800'
        : 'bg-blue-50 border-blue-200 text-blue-800';
  return (
    <div role="alert" data-testid={testId} className={`p-3 rounded-lg text-sm border ${styles}`}>
      {message}
    </div>
  );
}
